import React, { useCallback, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import DOMPurify from 'dompurify';

import { Refinement } from '@/api/generated';
import XSmallIcon from '@/assets/icons/x-small.svg?react';
import ChipMultiSelect from '@/components/fields/ChipMultiSelect';
import ChipSelect from '@/components/fields/ChipSelect';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import LoadingButton from '@/components/LoadingButton';
import { yupValidationResolver } from '@/hooks/useYupValidationResolver';

import { getRefinementSchema } from './schema';

const CLOSE_ICON_SIZE = 32;
const MODAL_WIDTH = 548;

const sanitizeHTML = (html: string) => ({
  __html: DOMPurify.sanitize(html, { ALLOWED_TAGS: ['ol', 'ul', 'li', 'b', 'i', 'em', 'strong', 'p', 'br', 'a'] })
});

interface RefinementModalFormData {
  options: string[];
}

interface RefinementModalProps {
  refinement: Refinement;
  catalogItemName: string;
  onSubmit: (selectedOptions: string[]) => void;
  onClose: () => void;
}

const RefinementModal: React.FC<RefinementModalProps> = ({ refinement, catalogItemName, onSubmit, onClose }) => {
  const { t } = useTranslation(['settings', 'common', 'validation']);
  const { spacing } = useTheme();

  const validationSchema = useMemo(() => getRefinementSchema(refinement, t), [refinement, t]);

  const form = useForm<RefinementModalFormData>({
    resolver: yupValidationResolver(validationSchema),
    defaultValues: {
      options: []
    }
  });

  const { handleSubmit, formState, setValue } = form;
  const formOptions = useWatch({ control: form.control, name: 'options' });

  const handleFormSubmit = useCallback(
    async (data: RefinementModalFormData) => {
      await onSubmit(data.options);
      form.reset({ options: [] });
    },
    [onSubmit, form]
  );

  const options = useMemo(() => {
    const mappedOptions = refinement?.options?.map(({ id, label }) => ({ value: id!, label: label! })) || [];

    return { items: mappedOptions, length: mappedOptions.length };
  }, [refinement.options]);

  const sanitizedHtml = useMemo(() => sanitizeHTML(refinement.label || ''), [refinement.label]);

  const isSingleSelection = refinement.maximumOptions === 1;

  const chipSelectValue = useMemo(() => {
    if (options.length >= 20 || !isSingleSelection) {
      return null;
    }

    return formOptions && formOptions.length > 0 ? formOptions[0] : null;
  }, [options.length, isSingleSelection, formOptions]);

  const handleChipSelectChange = useCallback(
    (value: string | number | null) => {
      setValue('options', value != null ? [String(value)] : [], { shouldValidate: true });
    },
    [setValue]
  );

  return (
    <Dialog
      open={true}
      onClose={onClose}
      sx={{
        '& .RefinementModal-root': {
          width: MODAL_WIDTH
        }
      }}
      PaperProps={{
        className: 'RefinementModal-root'
      }}
    >
      <DialogTitle
        component={Stack}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        bgcolor="background.default"
        sx={{ p: spacing(1.5, 2, 2.25), position: 'relative' }}
      >
        <Box flexGrow={1} textAlign="center">
          {t('processSettings.form.tests.refinementModalTitle', { name: catalogItemName })}
        </Box>
        <IconButton
          title={t('common:actions.close')}
          sx={{
            p: 0,
            position: 'absolute',
            insetInlineEnd: spacing(2),
            top: spacing(2),
            color: 'text.primary',
            '& .XSmallIcon-root': { width: CLOSE_ICON_SIZE, height: CLOSE_ICON_SIZE }
          }}
          onClick={onClose}
        >
          <XSmallIcon className="XSmallIcon-root" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <FormProvider {...form}>
          <Stack gap={3} m={3} alignItems="center">
            <Typography variant="body3" color="grey.700" dangerouslySetInnerHTML={sanitizedHtml} />
            {isSingleSelection && options.length < 20 && (
              <ChipSelect value={chipSelectValue} options={options.items} onChange={handleChipSelectChange} />
            )}
            {!isSingleSelection && options.length < 20 && (
              <Field
                component={ChipMultiSelect}
                name="options"
                innerProps={{
                  options: options.items,
                  maxOptions: refinement.maximumOptions
                }}
              />
            )}
            {options.length >= 20 && (
              <Field
                component={DropdownSelect}
                name="options"
                innerProps={{
                  options: options.items,
                  isMulti: !isSingleSelection,
                  isSearchable: true,
                  usePortal: true,
                  maxOptions: refinement.maximumOptions,
                  sx: { minWidth: 320 }
                }}
              />
            )}
          </Stack>
          <Stack
            direction="row"
            sx={{ padding: spacing(2.5, 2, 2), gap: spacing(1.5), justifyContent: 'space-between' }}
          >
            <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
            <LoadingButton
              color="primary"
              autoFocus
              variant="contained"
              disabled={!formState.isValid}
              isLoading={formState.isSubmitting}
              onClick={handleSubmit(handleFormSubmit)}
            >
              {t('common:actions.next')}
            </LoadingButton>
          </Stack>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default RefinementModal;
